/**
 * SaveChatButton — "Save to Documents" UI for AI Hub threads.
 *
 * - Default: saves the chat transcript to the workspace's "AI Chats" folder
 *   (auto-created on first use) in Markdown.
 * - Power user: "Save as…" opens a dialog with format toggle (Markdown / PDF)
 *   and a folder picker with live search across all folders.
 *
 * Note: every send-message call already auto-saves the latest transcript into
 * the "AI Chats" folder — this button is the explicit/customize path.
 */

import { useState, useEffect, useMemo } from 'react';
import { apiFetch } from '../../fetchInterceptor';
import { isInEmergentPreview } from '../../lib/sandboxDetect';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuLabel,
} from '../ui/dropdown-menu';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { ScrollArea } from '../ui/scroll-area';
import {
  Save, FileText, FileDown, FolderOpen, Search, Loader2, CheckCircle2, Folder, Download,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function SaveChatButton({ threadId, workspaceId, triggerClassName }) {
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  const saveQuick = async () => {
    // Markdown removed (Feb 24 2026 — Markdown files were not opening cleanly
    // in the in-app document preview). Quick-save always uses PDF.
    const fmt = 'pdf';
    if (!workspaceId) { toast.error('Pick a workspace first.'); return; }
    setSaving(true);
    try {
      const r = await apiFetch(`${API_URL}/api/ai-hub/threads/${threadId}/save-to-documents`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ workspace_id: workspaceId, fmt }),
      });
      if (r.ok) {
        // Route the toast "Open" CTA to the specific folder the doc landed in
        // (the backend returns the folder_id on every save). Falls back to
        // /documents if the response shape ever changes.
        const data = await r.json().catch(() => ({}));
        const folderId = data?.document?.folder_id;
        const docId = data?.document?.document_id;
        const target = folderId
          ? `/documents?folder=${folderId}${docId ? `&doc=${docId}` : ''}`
          : '/documents';
        toast.success('Saved to AI Chats folder (PDF)', {
          action: { label: 'Open', onClick: () => { window.location.href = target; } },
        });
      } else {
        const e = await r.json().catch(() => ({}));
        toast.error(e.detail || 'Save failed');
      }
    } catch {
      toast.error('Save failed');
    } finally {
      setSaving(false);
    }
  };

  /**
   * Stream the chat directly to the user's computer as a real file.
   *
   * Why this two-step path (mint token → window.open into wrapper page):
   *   • The Emergent preview iframe sandbox silently blocks programmatic
   *     anchor[download].click() and even iframe-PDF inline blob navigation.
   *   • Chrome's PDF viewer ignores Content-Disposition: attachment when a
   *     URL has a `.pdf` extension, so a direct window.open of the raw PDF
   *     route renders the PDF inline and never saves to disk.
   *   • The wrapper page (/api/pdf/{id}?action=download) is plain HTML
   *     served in a top-level browser tab. Its inline auto-script clicks a
   *     real <a download> Save button, which triggers a true OS-level
   *     download. The tab then closes itself a couple seconds later.
   *
   * For non-PDF formats (md, txt) we use a direct anchor-click in the
   * current page — those have no PDF-viewer override and the iframe sandbox
   * passes plain text/markdown downloads through fine.
   */
  const downloadToComputer = async (ext) => {
    // Inside the Emergent preview iframe, the sandbox is missing the
    // `allow-downloads` flag, so every download mechanism — anchor.click,
    // window.open of a wrapper page, even programmatic Save-As — silently
    // fails. The fix has nothing to do with our code; the file simply
    // never reaches disk. Tell the user clearly and offer a one-click
    // escape route to a real browser tab.
    if (isInEmergentPreview()) {
      const openHere = `${window.location.origin}${window.location.pathname}${window.location.search}${window.location.hash}`;
      toast.message("Downloads don't work in this preview", {
        duration: 30000,
        description: 'Open the app in a real browser tab (or use your deployed site) — downloads work normally there.',
        action: {
          label: 'Open in new tab',
          onClick: () => { window.open(openHere, '_blank'); },
        },
      });
      return;
    }

    setSaving(true);
    try {
      const r = await apiFetch(
        `${API_URL}/api/ai-hub/threads/${threadId}/download-token?ext=${ext}`,
        { method: 'POST' },
      );
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        throw new Error(e.detail || 'Could not prepare download');
      }
      const { url: rawUrl, filename } = await r.json();

      if (ext === 'pdf') {
        // PDF path: download the bytes, stash them in the temp PDF store
        // (which serves a wrapper page that auto-triggers a real save),
        // then open the wrapper with action=download in a new top-level tab.
        const pdfResp = await fetch(rawUrl);
        if (!pdfResp.ok) throw new Error('Could not fetch PDF');
        const pdfBlob = await pdfResp.blob();

        const storeResp = await fetch(`${API_URL}/api/store-pdf`, {
          method: 'POST',
          headers: { 'X-Filename': filename },
          body: pdfBlob,
        });
        if (!storeResp.ok) throw new Error('Could not stage PDF for download');
        const { pdf_id } = await storeResp.json();

        const wrapperUrl = `${API_URL}/api/pdf/${pdf_id}?action=download`;
        const win = window.open(wrapperUrl, '_blank');
        if (win && !win.closed) {
          toast.success(`Downloading ${filename}`, {
            description: 'A new tab will open briefly while your browser saves the file.',
          });
        } else {
          // Popup blocked — show a manual fallback link.
          toast.message('Click to download', {
            duration: 30000,
            description: `Your browser blocked the auto-download. Click below to grab "${filename}".`,
            action: {
              label: 'Download now',
              onClick: () => { window.location.href = wrapperUrl; },
            },
          });
        }
      } else {
        // Markdown / TXT path: a regular anchor click works because the
        // server returns text/markdown or text/plain — browsers do not
        // try to render those inline.
        const a = document.createElement('a');
        a.href = rawUrl;
        a.download = filename;
        a.rel = 'noopener';
        document.body.appendChild(a);
        a.click();
        setTimeout(() => { try { document.body.removeChild(a); } catch { /* */ } }, 1000);
        toast.success(`✓ Saved to your computer`, {
          duration: 3500,
          description: `${filename} is in your browser's Downloads folder.`,
        });
      }
    } catch (e) {
      toast.error(e?.message || 'Download failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            disabled={saving}
            className={cn(
              'bg-white dark:bg-zinc-900 border-zinc-200 dark:border-white/10 text-zinc-700 dark:text-zinc-200 hover:bg-zinc-50 dark:hover:bg-zinc-800 gap-1.5',
              triggerClassName,
            )}
            data-testid="save-chat-btn"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            <span className="text-xs">Save</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-64">
          <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-zinc-500">Save to Documents</DropdownMenuLabel>
          <DropdownMenuItem onClick={saveQuick} className="cursor-pointer gap-2" data-testid="save-chat-quick-pdf">
            <FileDown className="w-3.5 h-3.5" />
            <div className="flex flex-col flex-1">
              <span className="text-sm">Save as PDF</span>
              <span className="text-[10px] text-muted-foreground">→ AI Chats folder (in-app)</span>
            </div>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setDialogOpen(true)} className="cursor-pointer gap-2" data-testid="save-chat-pick-folder">
            <FolderOpen className="w-3.5 h-3.5" />
            <span className="text-sm">Save to specific folder…</span>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-zinc-500">Download to my computer</DropdownMenuLabel>
          <DropdownMenuItem
            onClick={() => downloadToComputer('pdf')}
            className="cursor-pointer gap-2"
            data-testid="download-chat-pdf"
          >
            <Download className="w-3.5 h-3.5 text-emerald-500" />
            <div className="flex flex-col flex-1">
              <span className="text-sm">Download as PDF</span>
              <span className="text-[10px] text-muted-foreground">Branded, locked layout</span>
            </div>
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => downloadToComputer('md')}
            className="cursor-pointer gap-2"
            data-testid="download-chat-md"
          >
            <FileText className="w-3.5 h-3.5" />
            <div className="flex flex-col flex-1">
              <span className="text-sm">Download as Markdown</span>
              <span className="text-[10px] text-muted-foreground">Editable, small file</span>
            </div>
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => downloadToComputer('txt')}
            className="cursor-pointer gap-2"
            data-testid="download-chat-txt"
          >
            <FileText className="w-3.5 h-3.5" />
            <div className="flex flex-col flex-1">
              <span className="text-sm">Download as Text</span>
              <span className="text-[10px] text-muted-foreground">Plain text, no formatting</span>
            </div>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {dialogOpen && (
        <SaveChatDialog
          threadId={threadId}
          workspaceId={workspaceId}
          onClose={() => setDialogOpen(false)}
        />
      )}
    </>
  );
}

function SaveChatDialog({ threadId, workspaceId, onClose }) {
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedFolderId, setSelectedFolderId] = useState(null);
  const [fmt] = useState('pdf');  // PDF only — Markdown removed Feb 24 2026
  const [saving, setSaving] = useState(false);
  const [activeSpace, setActiveSpace] = useState('firm');

  useEffect(() => {
    const loadAll = async () => {
      if (!workspaceId) { setLoading(false); return; }
      setLoading(true);
      try {
        // Load folders across all spaces (firm/client/vendor/personal)
        const spaces = ['firm', 'client', 'vendor', 'personal'];
        const results = await Promise.all(spaces.map(s =>
          apiFetch(`${API_URL}/api/workspaces/${workspaceId}/folders?space=${s}`)
            .then(r => r.ok ? r.json() : [])
            .catch(() => [])
        ));
        const merged = [];
        spaces.forEach((s, i) => {
          (results[i] || []).forEach(f => {
            if (!f.is_deleted && !f.is_archived) merged.push({ ...f, _space: s });
          });
        });
        setFolders(merged);
      } finally {
        setLoading(false);
      }
    };
    loadAll();
  }, [workspaceId]);

  const filtered = useMemo(() => {
    let list = folders.filter(f => f._space === activeSpace);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(f => (f.name || '').toLowerCase().includes(q));
    }
    return list.slice(0, 200);
  }, [folders, search, activeSpace]);

  const handleSave = async () => {
    if (!selectedFolderId) { toast.error('Pick a folder.'); return; }
    setSaving(true);
    try {
      const r = await apiFetch(`${API_URL}/api/ai-hub/threads/${threadId}/save-to-documents`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ workspace_id: workspaceId, folder_id: selectedFolderId, fmt }),
      });
      if (r.ok) {
        const data = await r.json().catch(() => ({}));
        const folder = folders.find(f => f.folder_id === selectedFolderId);
        const docId = data?.document?.document_id;
        const target = `/documents?folder=${selectedFolderId}${docId ? `&doc=${docId}` : ''}`;
        toast.success(`Saved to ${folder?.name || 'folder'} (PDF)`, {
          action: { label: 'Open', onClick: () => { window.location.href = target; } },
        });
        onClose();
      } else {
        toast.error('Save failed');
      }
    } catch {
      toast.error('Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      {/* Near-full-screen save modal: 1200px wide on desktop, w-[95vw] on smaller
        * screens. Two-column body: spaces (left) + folder tree (right). Format
        * + Save action live in the right column footer so the folder list stays
        * the main focus. */}
      <DialogContent
        className="sm:max-w-[1200px] w-[95vw] h-[88vh] max-h-[88vh] p-0 flex flex-col overflow-hidden"
        data-testid="save-chat-dialog"
      >
        <DialogHeader className="px-6 pt-6 pb-3 border-b">
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Save className="w-5 h-5 text-emerald-500" />
            Save chat to Documents
          </DialogTitle>
          <DialogDescription>
            Pick a folder and format. This chat will be saved as a document and kept in sync as the conversation continues.
          </DialogDescription>
        </DialogHeader>

        {/* Two-column body. `min-h-0` on the grid AND every flex-column
          * descendant is required so the ScrollArea below can shrink instead
          * of pushing the dialog past its 88vh max-height. Without these the
          * folder list silently overflows off-screen with no scrollbar — the
          * "I can't scroll down" bug the user hit in prod. */}
        <div className="flex-1 min-h-0 grid grid-cols-[260px_minmax(0,1fr)] gap-0 overflow-hidden">
          {/* LEFT — space tree */}
          <aside className="border-r bg-muted/30 dark:bg-zinc-900/30 flex flex-col min-h-0">
            <div className="px-4 py-3 border-b">
              <p className="text-[10px] font-mono uppercase tracking-[0.22em] text-muted-foreground">
                Spaces
              </p>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto p-2 space-y-1">
              {[
                { key: 'firm',     label: 'Firm',      desc: 'Shared org files' },
                { key: 'client',   label: 'Customers', desc: 'Customer files' },
                { key: 'vendor',   label: 'Vendors',   desc: 'Vendor files' },
                { key: 'personal', label: 'Personal',  desc: 'Your private files' },
              ].map((s) => {
                const count = folders.filter((f) => f._space === s.key).length;
                return (
                  <button
                    key={s.key}
                    type="button"
                    onClick={() => setActiveSpace(s.key)}
                    className={cn(
                      'w-full text-left px-3 py-2 rounded-lg transition-colors flex items-start gap-2',
                      activeSpace === s.key
                        ? 'bg-emerald-50 dark:bg-emerald-950/30 ring-1 ring-emerald-500/40 text-foreground'
                        : 'hover:bg-muted/60 text-muted-foreground',
                    )}
                    data-testid={`save-space-${s.key}`}
                  >
                    <FolderOpen className={cn('w-4 h-4 mt-0.5 flex-shrink-0', activeSpace === s.key && 'text-emerald-500')} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium flex items-center gap-1.5">
                        {s.label}
                        <span className="ml-auto text-[10px] font-mono text-muted-foreground">{count}</span>
                      </div>
                      <p className="text-[10px] leading-tight mt-0.5 opacity-70">{s.desc}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          </aside>

          {/* RIGHT — search, folder list, format toggle, action footer */}
          <div className="flex flex-col min-w-0 min-h-0">
            {/* Search */}
            <div className="px-6 pt-4 pb-3 border-b flex-shrink-0">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder={`Search folders in ${activeSpace}…`}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9 h-10"
                  data-testid="save-chat-search"
                  autoFocus
                />
              </div>
            </div>

            {/* Folder list — explicit min-h-0 so the ScrollArea actually
              * scrolls instead of forcing the parent grid to grow. The
              * inner Radix viewport needs an explicit height ceiling from
              * its ancestors to enable the wheel/touch scroll handler. */}
            <div className="flex-1 min-h-0 overflow-hidden">
              <ScrollArea className="h-full px-3 py-2" data-testid="save-folder-scroll">
                <div className="space-y-0.5 pb-2">
                {loading ? (
                  <p className="text-sm text-muted-foreground text-center py-12">Loading folders…</p>
                ) : filtered.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-12 italic">
                    {search ? `No folders match "${search}"` : 'No folders in this space yet.'}
                  </p>
                ) : filtered.map(f => (
                  <button
                    type="button"
                    key={f.folder_id}
                    onClick={() => setSelectedFolderId(f.folder_id)}
                    className={cn(
                      'w-full text-left px-3 py-2.5 rounded-lg flex items-center gap-2.5 transition-colors',
                      selectedFolderId === f.folder_id
                        ? 'bg-emerald-50 dark:bg-emerald-950/30 ring-1 ring-emerald-500'
                        : 'hover:bg-muted/40',
                    )}
                    data-testid={`save-folder-option-${f.folder_id}`}
                  >
                    <Folder className="w-4 h-4 flex-shrink-0" style={{ color: f.color || undefined }} />
                    <span className="text-sm flex-1 truncate">{f.name}</span>
                    {f.is_contact_root && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                        {f._space === 'client' ? 'Customer' : 'Vendor'}
                      </span>
                    )}
                    {selectedFolderId === f.folder_id && <CheckCircle2 className="w-4 h-4 text-emerald-500" />}
                  </button>
                ))}
                </div>
              </ScrollArea>
            </div>

            {/* PDF-only footer notice (Markdown removed Feb 24 2026) */}
            <div className="px-6 py-3 border-t bg-muted/20 flex items-center gap-2 flex-shrink-0">
              <FileDown className="w-3.5 h-3.5 text-emerald-500" />
              <span className="text-xs">
                Saving as <strong>PDF</strong> · Locked &amp; professional · Opens in any browser
              </span>
            </div>
          </div>
        </div>

        <DialogFooter className="px-6 py-4 border-t bg-background">
          <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button
            onClick={handleSave}
            disabled={!selectedFolderId || saving}
            className="bg-emerald-600 hover:bg-emerald-500 text-white gap-1.5"
            data-testid="save-chat-confirm"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            Save here
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
